// Catalogue de profils PHY candidats pour LumiChat.
//
// Les associations PHY/modulation suivent la famille OCC IEEE 802.15.7-2018.
// Les tables normatives de modes et de débits n'ont pas encore été vérifiées
// dans ce dépôt : aucune valeur ci-dessous ne doit être annoncée comme une
// conformité IEEE. Les plages sont des enveloppes d'ingénierie calculables.

export const PROFILE_CONFORMANCE = 'adaptation-until-normative-table-verified';

const STANDARD_FAMILY = 'IEEE 802.15.7-2018 OCC';
const RATE_RANGE_STATUS = 'engineering-envelope-not-normative';

const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

function positiveNumber(value, name) {
  if (!isFiniteNumber(value) || value <= 0) {
    throw new RangeError(`${name} must be a finite number greater than zero`);
  }
  return Number(value);
}

function positiveInteger(value, name) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }
  return Number(value);
}

function efficiency(value, name) {
  if (!isFiniteNumber(value) || value <= 0 || value > 1) {
    throw new RangeError(`${name} must be in the interval (0, 1]`);
  }
  return Number(value);
}

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

/**
 * Estimation A-QL/C-OOK : cellules spatiales x bits/cellule x symboles/s,
 * puis rendements FEC, RLL et part applicative après overhead.
 */
export function estimateSpatialApplicationRate({
  gridCells,
  bitsPerCell,
  symbolHz,
  fecRate = 1,
  rllRate = 1,
  overheadEfficiency = 1,
}) {
  const cells = positiveInteger(gridCells, 'gridCells');
  const bits = positiveNumber(bitsPerCell, 'bitsPerCell');
  const symbols = positiveNumber(symbolHz, 'symbolHz');
  const fec = efficiency(fecRate, 'fecRate');
  const rll = efficiency(rllRate, 'rllRate');
  const overhead = efficiency(overheadEfficiency, 'overheadEfficiency');
  const grossBps = cells * bits * symbols;
  const combinedEfficiency = fec * rll * overhead;

  return Object.freeze({
    grossBps,
    netBps: grossBps * combinedEfficiency,
    combinedEfficiency,
  });
}

/**
 * Estimation SS2DC/QR : octets utiles par code x codes simultanés x codes/s,
 * puis rendement FEC. L'overhead QR doit déjà être retranché de bytesPerCode.
 */
export function estimateSs2dcApplicationRate({
  bytesPerCode,
  codesPerFrame = 1,
  codeHz,
  fecRate = 1,
}) {
  const bytes = positiveInteger(bytesPerCode, 'bytesPerCode');
  const codes = positiveInteger(codesPerFrame, 'codesPerFrame');
  const frequency = positiveNumber(codeHz, 'codeHz');
  const fec = efficiency(fecRate, 'fecRate');
  const grossBps = bytes * 8 * codes * frequency;

  return Object.freeze({
    grossBps,
    netBps: grossBps * fec,
    combinedEfficiency: fec,
  });
}

/** Débit du framing Web‑Torch réellement implémenté, Manchester compris. */
export function estimateTorchManchesterRate({
  chipMs,
  envelopeBytes,
  framingBits = 104,
  guardChips = 16,
}) {
  const chipDuration = positiveNumber(chipMs, 'chipMs');
  const bytes = positiveInteger(envelopeBytes, 'envelopeBytes');
  const overheadBits = positiveInteger(framingBits, 'framingBits');
  const guards = positiveInteger(guardChips, 'guardChips');
  const envelopeBits = bytes * 8;
  const totalChips = (overheadBits + envelopeBits) * 2 + guards;
  const durationSeconds = totalChips * chipDuration / 1000;
  const grossBps = 1000 / (2 * chipDuration);
  const netBps = envelopeBits / durationSeconds;
  return Object.freeze({
    grossBps,
    netBps,
    combinedEfficiency: netBps / grossBps,
    totalChips,
    durationMs: durationSeconds * 1000,
  });
}

const RATE_ENVELOPES = deepFreeze({
  'screen-mono': {
    parameters: {
      bytesPerCode: { min: 1, max: 2953 },
      codesPerFrame: { min: 1, max: 4 },
      codeHz: { min: 0.5, max: 15 },
      fecRate: { min: 0.25, max: 1 },
    },
    grossBps: { min: 4, max: 1_417_440 },
    netBps: { min: 1, max: 1_417_440 },
  },
  'screen-color': {
    parameters: {
      bytesPerCode: { min: 1, max: 2953 },
      codesPerFrame: { min: 1, max: 1 },
      codeHz: { min: 0.5, max: 15 },
      fecRate: { min: 0.25, max: 1 },
    },
    grossBps: { min: 4, max: 354_360 },
    netBps: { min: 1, max: 354_360 },
  },
  flashlight: {
    parameters: {
      chipMs: { min: 80, max: 600 },
      envelopeBytes: { min: 19, max: 259 },
      framingBits: 104,
      guardChips: 16,
    },
    grossBps: { min: 0.8333333333333334, max: 6.25 },
    netBps: { min: 0.4797979797979798, max: 5.92948717948718 },
  },
});

export const PROFILE_CATALOG = deepFreeze({
  'screen-mono': {
    id: 'screen-mono',
    label: 'Screen monochrome',
    standardFamily: STANDARD_FAMILY,
    phy: 'PHY VI',
    modulation: 'SS2DC',
    variant: 'QR',
    implementedCarrier: 'ISO/IEC 18004 QR monochrome',
    publicD4Candidate: {
      source: 'IEEE P802.15-17-0488-00-007a (non-binding contribution)',
      opticalClockHz: 30,
      bitRatesBps: [368_000],
    },
    conformance: PROFILE_CONFORMANCE,
    rateModel: 'ss2dc',
    theoreticalRateRangeBps: {
      status: RATE_RANGE_STATUS,
      unit: 'bit/s',
      grossFormula: 'bytesPerCode * 8 * codesPerFrame * codeHz',
      netFormula: 'grossBps * fecRate',
      ...RATE_ENVELOPES['screen-mono'],
    },
    hardwarePrerequisites: [
      'measured-camera-frame-rate',
      'measured-display-refresh-rate',
      'camera-resolved-monochrome-code-grid',
    ],
    admission: {
      minimumCameraFramesPerCode: 2,
      minimumDisplayFramesPerCode: 1,
      defaultCodeHz: 2,
    },
  },
  'screen-color': {
    id: 'screen-color',
    label: 'Screen color',
    standardFamily: STANDARD_FAMILY,
    phy: 'PHY VI',
    modulation: 'SS2DC',
    variant: 'QR palette séquentielle',
    implementedCarrier: 'one ISO/IEC 18004 QR per packet with a high-contrast pastel palette',
    publicD4Candidate: {
      source: 'IEEE P802.15-17-0488-00-007a A-QL row (non-binding contribution)',
      referenceMode: 'A-QL',
      opticalClockHz: 10,
      bitRatesBps: [1_320, 5_540],
    },
    publicColorProposal: {
      source: 'IEEE P802.15-16-0012-01-007a (non-binding QR-ISC/C-CSK proposal)',
      symbolHz: 10,
      bitRatesBps: [2_500, 5_000, 7_500, 40_000, 80_000],
    },
    conformance: PROFILE_CONFORMANCE,
    rateModel: 'ss2dc',
    theoreticalRateRangeBps: {
      status: RATE_RANGE_STATUS,
      unit: 'bit/s',
      grossFormula: 'bytesPerCode * 8 * codesPerFrame * codeHz',
      netFormula: 'grossBps * fecRate',
      ...RATE_ENVELOPES['screen-color'],
    },
    hardwarePrerequisites: [
      'measured-camera-frame-rate',
      'measured-display-refresh-rate',
      'camera-resolved-monochrome-code-grid',
    ],
    admission: {
      minimumCameraFramesPerCode: 2,
      minimumDisplayFramesPerCode: 1,
      defaultCodeHz: 1.4,
    },
  },
  flashlight: {
    id: 'flashlight',
    label: 'Flashlight',
    standardFamily: STANDARD_FAMILY,
    phy: 'PHY V',
    modulation: 'Web-Torch OOK',
    variant: 'Manchester expérimental',
    implementedCarrier: 'browser-controlled phone torch with measured adaptive chip timing',
    publicD4Candidate: {
      source: 'IEEE P802.15-17-0488-00-007a (non-binding contribution)',
      opticalClockHz: [2_200, 4_400],
      bitRatesBps: [40, 110, 220, 400],
    },
    conformance: PROFILE_CONFORMANCE,
    standardConformance: 'none-claimed',
    referenceOnly: 'IEEE PHY V C-OOK remains a comparison; it is not the implemented carrier',
    rateModel: 'torch-manchester',
    theoreticalRateRangeBps: {
      status: RATE_RANGE_STATUS,
      unit: 'bit/s',
      grossFormula: '1000 / (2 * chipMs)',
      netFormula: '(envelopeBytes * 8) / (((104 + envelopeBytes * 8) * 2 + 16) * chipMs / 1000)',
      ...RATE_ENVELOPES.flashlight,
    },
    hardwarePrerequisites: [
      'measured-camera-frame-rate',
      'browser-controllable-torch',
      'measured-torch-toggle-p95',
      'monotonic-high-resolution-clock',
    ],
    admission: {
      minimumCameraFramesPerChip: 2.25,
      defaultSymbolHz: 10,
    },
  },
});

export const PROFILE_IDS = Object.freeze(Object.keys(PROFILE_CATALOG));

export function getProfile(profileId) {
  const profile = PROFILE_CATALOG[profileId];
  if (!profile) throw new RangeError(`unknown optical profile: ${profileId}`);
  return profile;
}

export function estimateProfileApplicationRate(profileId, parameters) {
  const profile = getProfile(profileId);
  if (profile.rateModel === 'ss2dc') return estimateSs2dcApplicationRate(parameters);
  if (profile.rateModel === 'torch-manchester') return estimateTorchManchesterRate(parameters);
  return estimateSpatialApplicationRate(parameters);
}

function capabilityNumber(capabilities, key) {
  const value = capabilities?.[key];
  return isFiniteNumber(value) && value > 0 ? Number(value) : null;
}

function requestedRate(profile, request) {
  if (profile.rateModel === 'ss2dc') {
    const codeHz = request?.codeHz ?? profile.admission.defaultCodeHz;
    return { key: 'codeHz', value: positiveNumber(codeHz, 'codeHz') };
  }
  const symbolHz = request?.symbolHz ?? profile.admission.defaultSymbolHz;
  return { key: 'symbolHz', value: positiveNumber(symbolHz, 'symbolHz') };
}

/**
 * Évalue l'admissibilité matérielle d'un profil sans modifier de périphérique.
 * Les raisons sont des identifiants stables destinés aux diagnostics/tests.
 */
export function evaluateProfileAdmission(profileId, capabilities = {}, request = {}) {
  const profile = getProfile(profileId);
  const reasons = [];
  const measuredCameraFps = capabilityNumber(capabilities, 'measuredCameraFps');
  const rate = requestedRate(profile, request);

  if (measuredCameraFps === null) reasons.push('camera-fps-unavailable');

  if (profileId === 'screen-mono') {
    const displayHz = capabilityNumber(capabilities, 'displayHz');
    if (displayHz === null) reasons.push('display-hz-unavailable');
    if (measuredCameraFps !== null
      && rate.value * profile.admission.minimumCameraFramesPerCode > measuredCameraFps) {
      reasons.push('camera-fps-insufficient-for-code-rate');
    }
    if (displayHz !== null
      && rate.value * profile.admission.minimumDisplayFramesPerCode > displayHz) {
      reasons.push('display-hz-insufficient-for-code-rate');
    }
  }

  if (profileId === 'screen-color') {
    const displayHz = capabilityNumber(capabilities, 'displayHz');
    if (displayHz === null) reasons.push('display-hz-unavailable');
    if (measuredCameraFps !== null
      && rate.value * profile.admission.minimumCameraFramesPerCode > measuredCameraFps) {
      reasons.push('camera-fps-insufficient-for-code-rate');
    }
    if (displayHz !== null
      && rate.value * profile.admission.minimumDisplayFramesPerCode > displayHz) {
      reasons.push('display-hz-insufficient-for-code-rate');
    }
  }

  if (profileId === 'flashlight') {
    if (capabilities.torchAvailable !== true) reasons.push('browser-torch-unavailable');
    const toggleP95 = capabilities.torchToggleP95Ms;
    const clockResolution = capabilities.clockResolutionMs;

    if (!isFiniteNumber(toggleP95) || toggleP95 < 0) {
      reasons.push('torch-toggle-p95-unavailable');
    }

    if (capabilities.monotonicClock !== true
      || !isFiniteNumber(clockResolution) || clockResolution < 0) {
      reasons.push('browser-clock-unavailable');
    }
  }

  return Object.freeze({
    profileId,
    admissible: reasons.length === 0,
    requestedRate: Object.freeze(rate),
    reasons: Object.freeze(reasons),
  });
}

export function evaluateProfiles(capabilities = {}, requests = {}) {
  return Object.freeze(PROFILE_IDS.map(profileId => (
    evaluateProfileAdmission(profileId, capabilities, requests[profileId])
  )));
}

export function selectAdmissibleProfiles(capabilities = {}, requests = {}) {
  return Object.freeze(evaluateProfiles(capabilities, requests)
    .filter(result => result.admissible)
    .map(result => result.profileId));
}
